import { useState } from "react";
import {
  CheckIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  EyeSlashIcon,
  KeyIcon,
} from "@heroicons/react/24/outline";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { capitalize } from "../../../utils/capitalize.utils";
import { useToast } from "../../../components/toast";

type CredentialsProps = {
  firstName?: string;
  lastName?: string;
  username: string;
  password: string;
  className?: string;
  ownCredentials?: boolean;
};

export function TeamMemberCredentials({
  firstName,
  lastName,
  username,
  password,
  className,
  ownCredentials,
}: CredentialsProps) {
  const { addToast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [copiedField, setCopiedField] = useState<
    "username" | "password" | null
  >(null);

  const copyToClipboard = async (
    text: string,
    field: "username" | "password"
  ) => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    addToast({
      message: `${
        field === "username" ? "Usuario copiado" : "Contraseña copiada"
      }`,
      type: "info",
      location: "bottom-center",
      duration: 1500,
    });
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div
      className={`p-6 rounded-lg mb-4 bg-blue-50 border border-blue-200 shadow-md ${className}`}
    >
      <div className="flex items-center gap-2 mb-4">
        <KeyIcon className="w-6 h-6 text-blue-600" />
        <h3 className="text-xl font-semibold text-gray-900">
          {ownCredentials
            ? "Tus credenciales de acceso"
            : firstName && lastName
            ? `Credenciales de ${capitalize(firstName)} ${capitalize(lastName)}`
            : "Credenciales del usuario creado"}
        </h3>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 flex items-center">
        <ExclamationTriangleIcon className="w-6 h-6 text-yellow-600 mr-1" />
        <p className="text-yellow-800 text-base">
          <strong>Importante:</strong>{" "}
          {ownCredentials
            ? "Guarda estas credenciales en un lugar seguro. No podrás volver a ver tu contraseña."
            : "Estas credenciales solo se mostrarán una vez. Asegúrate de copiarlas antes de salir de esta página."}
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between bg-white p-4 rounded-md border border-gray-200">
          <div className="flex-1">
            <label className="text-base text-gray-700">
              {ownCredentials ? "Tu nombre de usuario" : "Nombre de usuario"}
            </label>
            <div className="font-mono text-base text-gray-900">{username}</div>
          </div>
          <button
            onClick={() => copyToClipboard(username, "username")}
            className="ml-4 p-2 rounded-md bg-blue-500 text-white hover:bg-blue-600"
            title="Copiar correo"
          >
            {copiedField === "username" ? (
              <CheckIcon className="w-6 h-6" />
            ) : (
              <DocumentDuplicateIcon className="w-6 h-6" />
            )}
          </button>
        </div>

        <div className="flex items-center justify-between bg-white p-4 rounded-md border border-gray-200">
          <div className="flex-1">
            <label className="text-base text-gray-700">
              {ownCredentials ? "Tu contraseña" : "Contraseña"}
            </label>
            <div className="font-mono text-base text-gray-900">
              {showPassword ? password : "••••••••"}
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <button
              onClick={() => setShowPassword(!showPassword)}
              className="p-2 rounded-md bg-blue-500 text-white hover:bg-blue-600"
              title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
            >
              {showPassword ? (
                <EyeSlashIcon className="w-6 h-6" />
              ) : (
                <EyeIcon className="w-6 h-6" />
              )}
            </button>
            <button
              onClick={() => copyToClipboard(password, "password")}
              className="p-2 rounded-md bg-blue-500 text-white hover:bg-blue-600"
              title="Copiar contraseña"
            >
              {copiedField === "password" ? (
                <CheckIcon className="w-6 h-6" />
              ) : (
                <DocumentDuplicateIcon className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      <p className="mt-4 text-base text-gray-700 italic">
        {ownCredentials
          ? "Guarda estas credenciales en un lugar seguro."
          : firstName && lastName
          ? `Por favor, comparte estas credenciales con ${firstName.toUpperCase()} ${lastName.toUpperCase()} para que pueda acceder a su cuenta.`
          : "Por favor, comparte las credenciales con el usuario para que pueda acceder a su cuenta."}
      </p>
    </div>
  );
}
